import React, { useState, useEffect } from 'react';
import { ChevronRight, Star, Shield, Users, Sparkles, ArrowRight, Menu, X } from 'lucide-react';import "./App.css"
const App = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking a link
  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  // Rest of the data (testimonials and stats) remains the same...
  const testimonials = [
    {
      text: "JourneyAI has completely transformed how I document my life. The AI insights are incredible!",
      author: "Sarah M.",
      role: "Writer"
    },
    {
      text: "The perfect blend of journaling and technology. It's like having a personal therapist.",
      author: "Michael K.",
      role: "Designer"
    },
    {
      text: "I've never been more consistent with journaling. The AI prompts keep me engaged daily.",
      author: "Jessica R.",
      role: "Entrepreneur"
    }
  ];

  const stats = [
    { number: "10K+", label: "Active Users" },
    { number: "4.8", label: "App Store Rating" },
    { number: "100K+", label: "Journal words Entries" }
  ];
  return (
    <div className="app">
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-content">
          <div className="logo">JourneyAI</div>
          
          {/* Desktop Navigation */}
          <div className="nav-links desktop-nav">
            <a href="#features">Features</a>
            <a href="#benefits">Benefits</a>
            <a href="#testimonials">Reviews</a>
            <a href="#perfect-for">Who It's For</a>
          </div>
          
          <div className="nav-right">
            <div className="download-buttons desktop-nav">
              <a href="https://apps.apple.com/ca/app/journeyai-your-life-story/id6714479989" target="_blank" // This will open the link in a new tab
        rel="noopener noreferrer" // This helps prevent security risks
         className="app-store-button">App Store</a>
              <a href="/android-test" className="play-store-button">Google Play</a>
            </div>
            
            {/* Hamburger Menu Button */}
            <button 
              className="mobile-menu-button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Overlay */}
        <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-content">
            <div className="mobile-nav-links">
              <a href="#features" onClick={handleMobileNavClick}>Features</a>
              <a href="#benefits" onClick={handleMobileNavClick}>Benefits</a>
              <a href="#testimonials" onClick={handleMobileNavClick}>Reviews</a>
              <a href="#perfect-for" onClick={handleMobileNavClick}>Who It's For</a>
            </div>
            <div className="mobile-nav-buttons">
              <a  href="https://apps.apple.com/ca/app/journeyai-your-life-story/id6714479989" target="_blank" // This will open the link in a new tab
        rel="noopener noreferrer"
              
              className="app-store-button" onClick={handleMobileNavClick}>
                Download on App Store
              </a>
              <a href="/android-test" className="play-store-button" onClick={handleMobileNavClick}>
                Get it on Google Play
              </a>
            </div>
          </div>
        </div>
      </nav>

      <header className="hero">
        <div className="hero-content">
          <div className="badge">
            <Star className="w-4 h-4" />
            <span>Featured in TechCrunch</span>
          </div>
          <h1>Your Life's Story,<br />Told with AI</h1>
          <p className="hero-subtitle">
            Transform your journaling experience with AI-powered insights, emotional tracking, 
            and personalized writing prompts.
          </p>
          <div className="hero-cta">
            <a href="https://apps.apple.com/ca/app/journeyai-your-life-story/id6714479989" target="_blank" // This will open the link in a new tab
        rel="noopener noreferrer"
             className="app-store-button">
              Download on App Store
              <ChevronRight className="w-4 h-4 ml-2" />
            </a>
            <a href="/android-test" className="play-store-button">
              Get it on Google Play
              <ChevronRight className="w-4 h-4 ml-2" />
            </a>
          </div>
          <div className="hero-stats">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-image">
          <div className="phone-mockup">
            <div className="screen-content">
              {/* Add app screenshot or animation here */}
            </div>
          </div>
          <div className="floating-card card-1">
            <Sparkles className="w-4 h-4" />
            <span>AI-Powered Insights</span>
          </div>
          <div className="floating-card card-2">
            <Shield className="w-4 h-4" />
            <span>End-to-End Encryption</span>
          </div>
        </div>
      </header>

      <section id="features" className="features">
        <h2>Features that Transform Your Journey</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <Sparkles className="w-8 h-8" />
            </div>
            <h3>AI Writing Assistant</h3>
            <p>Get personalized prompts and insights that help you dive deeper into your thoughts.</p>
            <a href="#" className="feature-link">
              Learn more <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <Users className="w-8 h-8" />
            </div>
            <h3>Mood Tracking</h3>
            <p>Visualize your emotional journey with advanced sentiment analysis and patterns.</p>
            <a href="#" className="feature-link">
              Learn more <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <Shield className="w-8 h-8" />
            </div>
            <h3>Private & Secure</h3>
            <p>Your thoughts are protected with military-grade encryption and local storage.</p>
            <a href="#" className="feature-link">
              Learn more <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <section id="testimonials" className="testimonials">
        <h2>What Our Users Say</h2>
        <div className="testimonials-carousel">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className={`testimonial-card ${index === activeTestimonial ? 'active' : ''}`}
            >
              <p className="testimonial-text">{testimonial.text}</p>
              <div className="testimonial-author">
                <div className="author-avatar"></div>
                <div className="author-info">
                  <h4>{testimonial.author}</h4>
                  <p>{testimonial.role}</p>
                </div>
              </div>
            </div>
          ))}
          <div className="carousel-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === activeTestimonial ? 'active' : ''}`}
                onClick={() => setActiveTestimonial(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>Start Your Journey Today</h2>
          <p>Join thousands of others who have transformed their journaling experience.</p>
          <div className="cta-buttons">
            <a  href="https://apps.apple.com/ca/app/journeyai-your-life-story/id6714479989" target="_blank" // This will open the link in a new tab
        rel="noopener noreferrer"
             className="app-store-button">
              Download on App Store
              <ChevronRight className="w-4 h-4 ml-2" />
            </a>
            <a href="/android-test" className="play-store-button">
              Get it on Google Play
              <ChevronRight className="w-4 h-4 ml-2" />
            </a>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-logo">JourneyAI</div>
            <p className="footer-tagline">Your life's story, enhanced by AI</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <a href="#about">About</a>
              <a href="#blog">Blog</a>
              <a href="#careers">Careers</a>
            </div>
            <div className="footer-column">
              <h4>Legal</h4>
              <a href="#privacy">Privacy</a>
              <a href="#terms">Terms</a>
              <a href="#security">Security</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2024 JourneyAI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default App;